import React, { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiErrorDetail } from '../../lib/adminApi';

export const ImageUpload = ({ label, value, onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/admin/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      {label && <label className="block text-xs tracking-wider uppercase text-[#757B7F] mb-1.5">{label}</label>}
      <div className="flex items-start gap-3">
        {value ? (
          <div className="relative">
            <img src={value} alt="preview" className="w-28 h-20 object-cover border border-[#E6E2DC]" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute -top-2 -right-2 bg-white border border-[#E6E2DC] rounded-full p-0.5 text-[#757B7F] hover:text-red-500"
              data-testid={`${testId}-remove`}
              aria-label="Remove image"
            >
              <X size={12} />
            </button>
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 border border-[#E6E2DC] bg-white px-4 py-2.5 text-xs text-[#2C2C2C] hover:border-[#DE92A9] transition-colors disabled:opacity-50"
          data-testid={testId}
        >
          <Upload size={13} className="text-[#DE92A9]" />
          {uploading ? 'Uploading...' : value ? 'Replace image' : 'Upload image'}
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
      </div>
    </div>
  );
};

export const GalleryUpload = ({ value = [], onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const urls = [...value];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/api/admin/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        urls.push(data.url);
      } catch (err) {
        toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail) || 'upload failed'}`);
      }
    }
    onChange(urls);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <label className="block text-xs tracking-wider uppercase text-[#757B7F] mb-1.5">Gallery</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((url, i) => (
          <div key={`${url}-${i}`} className="relative">
            <img src={url} alt={`gallery ${i + 1}`} className="w-20 h-14 object-cover border border-[#E6E2DC]" />
            <button
              type="button"
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              className="absolute -top-2 -right-2 bg-white border border-[#E6E2DC] rounded-full p-0.5 text-[#757B7F] hover:text-red-500"
              aria-label="Remove gallery image"
            >
              <X size={11} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={uploading}
        className="inline-flex items-center gap-2 border border-[#E6E2DC] bg-white px-4 py-2 text-xs text-[#2C2C2C] hover:border-[#DE92A9] transition-colors disabled:opacity-50"
        data-testid={testId}
      >
        <Upload size={13} className="text-[#DE92A9]" />
        {uploading ? 'Uploading...' : 'Add gallery images'}
      </button>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={handleFiles} data-testid={`${testId}-input`} />
    </div>
  );
};
